import { api, Area, areaParams } from '../../api';

// Pool parties (c2n.api PoolPartyEndpoints.cs). Dates are "yyyy-MM-dd", times "HH:mm:ss", money whole rupees.

export const POOL_TYPES = ['Venue Pool', 'Resort / Hotel', 'Club', 'Private Villa', 'Other'] as const;

export type PoolPartyStatus = 'Draft' | 'Pending' | 'Rejected' | 'Suspended' | 'Cancelled' | 'Completed' | 'SoldOut' | 'Active';

export interface PublicPoolParty {
  id: number;
  restoId: number;
  venueName: string;
  address: string;
  latitude: number;
  longitude: number;
  distanceKm?: number | null;
  title: string;
  description: string;
  poolType: string;
  eventDate: string;
  startTime: string;
  endTime: string;
  pricePerGuest: number;
  capacity: number;
  spotsLeft: number;
  maxGuestsPerBooking: number;
  inclusions?: string | null;
  agePolicy?: string | null;
  dressCode?: string | null;
  safetyRules?: string | null;
  cancellationPolicy?: string | null;
  emergencyContact?: string | null;
  status: 'Active' | 'SoldOut';
  imageUrl?: string | null;
}

export interface PoolPartyBooking {
  id: number;
  poolPartyId: number;
  partyTitle: string;
  venueName: string;
  address: string;
  eventDate: string;
  startTime: string;
  endTime: string;
  guests: number;
  pricePerGuest: number;
  status: 'Confirmed' | 'Cancelled' | 'CheckedIn';
  // Entry code ("ABCD-EFGH"); only while the booking is confirmed.
  code?: string | null;
  canCancel: boolean;
  partyCancelled: boolean;
  cancelReason?: string | null;
  createdAt: string;
  checkedInAt?: string | null;
}

export interface PoolPartyInput {
  title: string;
  description: string;
  poolType: string;
  eventDate: string;
  startTime: string;
  endTime: string;
  capacity: number;
  pricePerGuest: number;
  maxGuestsPerBooking: number;
  inclusions?: string | null;
  agePolicy?: string | null;
  dressCode?: string | null;
  safetyRules?: string | null;
  cancellationPolicy?: string | null;
  emergencyContact?: string | null;
  isPublished: boolean;
}

export interface PartnerPoolParty extends PoolPartyInput {
  id: number;
  bookedGuests: number;
  checkedInGuests: number;
  status: PoolPartyStatus;
  moderationNote?: string | null;
  cancelReason?: string | null;
  updatedAt: string;
}

export interface PoolPartyGuest {
  bookingId: number;
  displayName: string;
  // Masked to the last 4 digits.
  mobile: string;
  guests: number;
  status: PoolPartyBooking['status'];
  bookedAt: string;
  checkedInAt?: string | null;
}

export interface CheckInResult {
  bookingId: number;
  poolPartyId: number;
  partyTitle: string;
  guestName: string;
  guests: number;
  checkedInAt: string;
}

export interface AdminPoolPartyRow {
  id: number;
  restoId: number;
  venueName: string;
  title: string;
  poolType: string;
  eventDate: string;
  startTime: string;
  endTime: string;
  capacity: number;
  bookedGuests: number;
  pricePerGuest: number;
  status: PoolPartyStatus;
  moderationStatus: 'Pending' | 'Approved' | 'Rejected' | 'Suspended';
  moderationNote?: string | null;
  description: string;
  agePolicy?: string | null;
  safetyRules?: string | null;
  emergencyContact?: string | null;
  updatedAt: string;
}

// ---------- Public + customers ----------

export const getPoolParties = async (lat?: number, lng?: number, area?: Area): Promise<PublicPoolParty[]> =>
  (await api.get<PublicPoolParty[]>('/api/pool-parties', { params: { ...(lat !== undefined && lng !== undefined ? { lat, lng } : {}), ...areaParams(area) } })).data;

export const getPoolParty = async (id: number): Promise<PublicPoolParty> => (await api.get<PublicPoolParty>(`/api/pool-parties/${id}`)).data;

export const bookPoolParty = async (id: number, guests: number): Promise<PoolPartyBooking> =>
  (await api.post<PoolPartyBooking>(`/api/pool-parties/${id}/bookings`, { guests })).data;

export const getMyPoolPartyBookings = async (): Promise<PoolPartyBooking[]> =>
  (await api.get<PoolPartyBooking[]>('/api/customer/pool-party-bookings')).data;

export const cancelPoolPartyBooking = async (id: number): Promise<PoolPartyBooking> =>
  (await api.post<PoolPartyBooking>(`/api/customer/pool-party-bookings/${id}/cancel`)).data;

// ---------- Partner portal (the admin console's ?restoId= is added by the shared api client) ----------

export const getMyPoolParties = async (): Promise<PartnerPoolParty[]> => (await api.get<PartnerPoolParty[]>('/api/account/pool-parties')).data;

export const createPoolParty = async (input: PoolPartyInput): Promise<PartnerPoolParty> =>
  (await api.post<PartnerPoolParty>('/api/account/pool-parties', input)).data;

export const savePoolParty = async (id: number, input: PoolPartyInput): Promise<PartnerPoolParty> =>
  (await api.put<PartnerPoolParty>(`/api/account/pool-parties/${id}`, input)).data;

export const cancelPoolParty = async (id: number, reason: string): Promise<PartnerPoolParty> =>
  (await api.post<PartnerPoolParty>(`/api/account/pool-parties/${id}/cancel`, { reason })).data;

export const deletePoolParty = async (id: number): Promise<void> => {
  await api.delete(`/api/account/pool-parties/${id}`);
};

export const getPoolPartyGuests = async (id: number): Promise<PoolPartyGuest[]> =>
  (await api.get<PoolPartyGuest[]>(`/api/account/pool-parties/${id}/guests`)).data;

export const checkInPoolPartyGuest = async (code: string): Promise<CheckInResult> =>
  (await api.post<CheckInResult>('/api/account/pool-parties/check-in', { code })).data;

// ---------- Admin ----------

export const getAdminPoolParties = async (): Promise<AdminPoolPartyRow[]> => (await api.get<AdminPoolPartyRow[]>('/api/admin/pool-parties')).data;

export const moderatePoolParty = async (id: number, action: ModerationAction, note?: string): Promise<{ message: string }> =>
  (await api.post<{ message: string }>(`/api/admin/pool-parties/${id}/moderate`, { action, note })).data;

export type ModerationAction = 'approve' | 'reject' | 'suspend';

// ---------- Formatting ----------

export const formatPartyDate = (iso: string) =>
  new Date(`${iso}T00:00:00`).toLocaleDateString('en-IN', { weekday: 'short', day: 'numeric', month: 'short' });

export const formatClock = (time: string) => {
  const [h, m] = time.split(':').map(Number);
  return new Date(2000, 0, 1, h, m).toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' });
};

export const partyWhen = (p: { eventDate: string; startTime: string; endTime: string }) =>
  `${formatPartyDate(p.eventDate)} · ${formatClock(p.startTime)} – ${formatClock(p.endTime)}`;

export const partyPrice = (pricePerGuest: number) =>
  pricePerGuest === 0 ? 'Free entry' : `₹${pricePerGuest.toLocaleString('en-IN')} per guest`;
